import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getReportSummary, getUsers, sendMonthlySummary } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Download, TrendingUp, Calendar, FileSpreadsheet, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  useEffect(() => {
    if (isAdmin) {
      (async () => {
        try {
          const { data } = await getUsers({ role: 'volunteer' });
          setUsers(data.users || []);
        } catch { /* ignore */ }
      })();
    }
  }, [isAdmin]);

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedUser) params.user_id = selectedUser;
      const { data } = await getReportSummary(params);
      setReport(data);
    } catch (e) {
      console.error('Failed to generate report', e);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = () => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (selectedUser && selectedUser !== 'all') params.set('user_id', selectedUser);
    window.open(`${API}/reports/pdf?${params.toString()}`, '_blank');
  };

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (selectedUser && selectedUser !== 'all') params.set('user_id', selectedUser);
    window.open(`${API}/reports/csv?${params.toString()}`, '_blank');
  };

  const handleSendMonthlySummary = async () => {
    setSendingSummary(true);
    try {
      const { data } = await sendMonthlySummary();
      const { toast } = await import('sonner');
      toast.success(`Monthly summary sent! ${data.total_hours}h total, ${data.emails_sent} emails sent to ${data.recipients} recipients.`);
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || 'Failed to send monthly summary');
    } finally {
      setSendingSummary(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">Reports</h1>
          <p className="text-[#5C5855] mt-1">Generate and download volunteer hour reports</p>
        </div>
        {user?.role === 'admin' && (
          <Button
            data-testid="send-monthly-summary-btn"
            onClick={handleSendMonthlySummary}
            disabled={sendingSummary}
            className="bg-[#3F6352] hover:bg-[#2C4A3C] text-white rounded-lg px-5 py-2.5 font-semibold"
          >
            {sendingSummary ? 'Sending...' : <><Mail className="h-4 w-4 mr-2" /> Send Monthly Summary</>}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E8E3DD] rounded-xl p-6 mb-6">
        <h2 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-[#D3735A]" /> Report Filters
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Start Date</Label>
            <Input
              data-testid="report-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-[#D1C9C0] bg-white"
            />
          </div>
          <div>
            <Label className="block text-sm font-semibold text-[#5C5855] mb-2">End Date</Label>
            <Input
              data-testid="report-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-[#D1C9C0] bg-white"
            />
          </div>
          {isAdmin && (
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Volunteer</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger data-testid="report-user-select" className="border-[#D1C9C0] bg-white">
                  <SelectValue placeholder="All volunteers" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Volunteers</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.volunteer_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="mt-4 flex gap-3">
          <Button
            data-testid="generate-report-button"
            onClick={generateReport}
            disabled={loading}
            className="bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-6 py-2.5 font-semibold"
          >
            {loading ? 'Generating...' : <><TrendingUp className="h-4 w-4 mr-2" /> Generate Report</>}
          </Button>
          {report && (
            <>
              <Button
                data-testid="download-pdf-button"
                onClick={downloadPdf}
                variant="outline"
                className="border-[#3F6352] text-[#3F6352] hover:bg-[#3F6352] hover:text-white rounded-lg px-6 py-2.5 font-semibold"
              >
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </Button>
              <Button
                data-testid="download-csv-button"
                onClick={downloadCsv}
                variant="outline"
                className="border-[#4A788C] text-[#4A788C] hover:bg-[#4A788C] hover:text-white rounded-lg px-6 py-2.5 font-semibold"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Report Results */}
      {report && (
        <div className="space-y-6" data-testid="report-results">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-[#E8E3DD] rounded-xl p-6">
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-[#5C5855]">Total Approved Hours</p>
              <p className="text-3xl font-bold text-[#2D2B2A] font-['Manrope'] mt-2" data-testid="report-total-hours">{report.total_hours}</p>
            </div>
            {report.volunteer_info && (
              <div className="bg-white border border-[#E8E3DD] rounded-xl p-6">
                <p className="text-xs tracking-[0.05em] uppercase font-bold text-[#5C5855]">Volunteer</p>
                <p className="text-lg font-bold text-[#2D2B2A] mt-2">{report.volunteer_info.name}</p>
                <p className="text-xs text-[#8A847F] font-mono">{report.volunteer_info.volunteer_id}</p>
              </div>
            )}
            <div className="bg-white border border-[#E8E3DD] rounded-xl p-6">
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-[#5C5855]">Period</p>
              <p className="text-sm text-[#2D2B2A] mt-2">{report.period?.start || 'Beginning'} to {report.period?.end || 'Present'}</p>
            </div>
          </div>

          {/* By Service Type */}
          {Object.keys(report.by_service_type || {}).length > 0 && (
            <div className="bg-white border border-[#E8E3DD] rounded-xl p-6">
              <h3 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4">Hours by Service Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(report.by_service_type).map(([type, hrs]) => (
                  <div key={type} className="bg-[#FAF8F5] rounded-lg p-4 border border-[#E8E3DD]">
                    <p className="text-sm font-medium text-[#5C5855]">{type}</p>
                    <p className="text-xl font-bold text-[#2D2B2A] font-['Manrope']">{hrs} hrs</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detailed Table */}
          <div className="w-full rounded-xl border border-[#E8E3DD] overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAF8F5] border-b border-[#E8E3DD]">
                  <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Date</TableHead>
                  <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Volunteer</TableHead>
                  <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Service Type</TableHead>
                  <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Hours</TableHead>
                  <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report.logs || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-[#8A847F]">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No approved hours found for this period</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  report.logs.map((log, idx) => (
                    <TableRow key={idx} className="border-b border-[#E8E3DD] hover:bg-[#F2EFEB]/50 transition-colors">
                      <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.date}</TableCell>
                      <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.volunteer_name}</TableCell>
                      <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.service_type}</TableCell>
                      <TableCell className="px-6 py-4 text-sm text-[#2D2B2A] font-semibold">{log.hours}</TableCell>
                      <TableCell className="px-6 py-4 text-sm text-[#5C5855]">{log.description || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
